import { BaseWebComponentView } from '../../framework/BaseWebComponentView';
import { GridViewSettings } from '../../grid/utils/types';
import { Column, ColumnKeys } from '../types';
import { RawEventsConfigurationGridDataProvider } from '../dataProvider/RawEventsConfigurationGridDataProvider';
import { osListRenderer } from './renderers';
import { isEmpty } from 'lodash';
import { GzToast, ToastPositionX, ToastPositionY, ToastSeverity } from '../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../ui/gzToastNotification';
import { GridFilterUiConfiguration } from '../../types/filters/types';
import { Filter } from '../../grid/gridFilters/types';
import { API_TYPE_RAW_EVENTS } from '../../webcomponents.constants';
import { GzSingleSelectComplexWebComponent } from '../../ui/gzSingleSelectComplex';

export class RawEventsConfigurationGridView extends BaseWebComponentView {
  private hasShownSplunkWarningToast = false;
  private hasAddonInfoToast = false;
  public companyFilterDropdown: GzSingleSelectComplexWebComponent | undefined;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$grid = this.shadow.querySelector('gz-grid-view');
    this.dom.$changeStatusSelector = this.shadow.querySelector('#changeStatusSelector');
    this.dom.$changeStatusButton = this.shadow.querySelector('#changeStatusButton');

    if (this.dom.$grid.injectDependencies) {
      this.dom.$grid.injectDependencies(this.dependencies);
      this.dom.$grid.start().then().catch(console.error);
    }
  }

  async buildSuccessView(viewSettings: GridViewSettings<Column, ColumnKeys>) {
    const view = viewSettings.defaultViewProps;

    // build the grid with data from the backend
    this.dom.$grid.propertiesSet = false;
    this.dom.$grid.dataProvider = new RawEventsConfigurationGridDataProvider(this.dependencies);
    this.dom.$grid.sideMenuParent = this.dependencies.gridSettingsSidePanelParent;

    this.dom.$grid.viewId = view.id;
    this.dom.$grid.allColumnDetails = viewSettings.allColumns;
    this.dom.$grid.columns = view.displayedColumns;
    this.dom.$grid.filters = view.filters;
    this.dom.$grid.filtersUiConfigs = this.getFiltersUiConfigs();
    this.dom.$grid.isCompactViewChecked = false;
    this.dom.$grid.defaultViewProps = viewSettings.defaultViewProps;
    this.dom.$grid.pageSize = view.pageSize;
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.OS_TYPES]: osListRenderer,
    };
    this.dom.$grid.predefinedFiltersKeys = viewSettings.predefinedFilterKeys;
    this.dom.$grid.propertiesSet = true;

    this.buildChangeStatusButton();
  }

  public updateHasSelectableRows(isMdrOn: boolean, hasEdrStorageAddon: boolean) {
    const vaadinGridSelColumn = this.dom.$grid.controller.view.dom.$grid.querySelector('vaadin-grid-selection-column');

    if (!isEmpty(vaadinGridSelColumn)) {
      if (isMdrOn || !hasEdrStorageAddon) {
        vaadinGridSelColumn.setAttribute('hidden', '');
      } else {
        vaadinGridSelColumn.removeAttribute('hidden');
      }
    }

    if (!this.hasShownSplunkWarningToast) {
      const toastMessage = isMdrOn
        ? this.dependencies.translateFn('MDR settings override the settings on this page.')
        : this.dependencies.translateFn(
            'The changes you perform on this page do not affect the endpoints with Security Telemetry enabled.'
          );
      this.buildInfoBoxToast(toastMessage, ToastSeverity.WARNING);
      this.hasShownSplunkWarningToast = true;
    }

    if (!hasEdrStorageAddon && !this.hasAddonInfoToast && !isMdrOn) {
      const toastMessage = this.dependencies.translateFn(
        'No license key detected. To gain access to this page, activate a GravityZone EDR Data Retention Add-on.'
      );
      this.buildInfoBoxToast(toastMessage, ToastSeverity.INFORMATIVE);
      this.hasAddonInfoToast = true;
    }
  }

  private buildInfoBoxToast(toastMessage: string, severity: ToastSeverity = ToastSeverity.INFORMATIVE) {
    const toastConfig: GzToast = {
      message: toastMessage,
      severity: severity,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
      keepOpen: true,
    };

    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig, this.dom.$wrapper);
  }

  getChangeStatusOptions(canEnable: boolean, canDisable: boolean) {
    return [
      {
        title: this.dependencies.translateFn('Enable'),
        value: true,
        disabled: !canEnable,
      },
      {
        title: this.dependencies.translateFn('Disable'),
        value: false,
        disabled: !canDisable,
      },
    ];
  }

  buildChangeStatusButton(): void {
    this.dom.$changeStatusSelector.options = this.getChangeStatusOptions(false, false);
    this.dom.$changeStatusButton.innerHTML = `<div class="change-status-button-container"><div>${this.dependencies.translateFn(
      'change status'
    )}</div><span class="icon-view_more_down"></span></div>`;
  }

  public buildErrorView(error: Error) {
    this.dom.$wrapper.innerHTML = '';
  }

  getFiltersUiConfigs(): Array<GridFilterUiConfiguration> {
    return [
      {
        key: ColumnKeys.COMPANY,
        config: {
          apiType: API_TYPE_RAW_EVENTS,
          populateFilterCallback: async (filterElement: GzSingleSelectComplexWebComponent, newValue: Filter<ColumnKeys>[ColumnKeys]) => {
            // Fill the dropdown with the provided company via the module constructor
            if (!filterElement.value) {
              await filterElement.setFilterOptions({
                totalItemsCount: 1,
                items: [
                  {
                    value: this.dependencies.api.defaultCompany.id,
                    label: this.dependencies.api.defaultCompany.name,
                  },
                ],
              });
              filterElement.setFormValue(this.dependencies.api.defaultCompany.id);

              // Will be used later to reload the dropdown when coming back to the grid after the list of
              // companies may have changed
              this.companyFilterDropdown = filterElement;
            } else if (newValue.hasOwnProperty('exactMatch') && filterElement.value !== newValue.exactMatch) {
              // If the value was reset by 'reset filters' or a similar event, we may need to change the displayed value
              filterElement.setFormValue(newValue.exactMatch);
            }
          },
        },
      },
    ];
  }
}
